/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { Alert, Box, Button, Divider, Grid, Stack, TextField, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { toast } from 'react-toastify';
import { patchApi } from 'views/services/api';

export default function StepHotelProfile({ hotel, hotelId, onNext, onBack }) {
  const [form, setForm] = useState({
    gstNumber: hotel?.gstNumber || '',
    roomgstpercentage: hotel?.roomgstpercentage ?? '',
    foodgstpercentage: hotel?.foodgstpercentage ?? '',
    contact: hotel?.contact || '',
    address: hotel?.address || '',
    checkInTime: hotel?.checkInTime || '14:00',
    checkOutTime: hotel?.checkOutTime || '12:00',
    cleaningBufferMinutes: hotel?.cleaningBufferMinutes ?? 30
  });
  const [saving, setSaving] = useState(false);

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleNext = async () => {
    setSaving(true);
    try {
      const payload = {
        gstNumber: form.gstNumber,
        address: form.address,
        checkInTime: form.checkInTime,
        checkOutTime: form.checkOutTime,
        cleaningBufferMinutes: Number(form.cleaningBufferMinutes) || 30
      };
      // contact ahora es String: admite el prefijo internacional (+58...)
      const contactTrimmed = String(form.contact).trim();
      if (contactTrimmed !== '') payload.contact = contactTrimmed;
      if (form.roomgstpercentage !== '') payload.roomgstpercentage = Number(form.roomgstpercentage);
      if (form.foodgstpercentage !== '') payload.foodgstpercentage = Number(form.foodgstpercentage);

      const res = await patchApi(`api/hotel/edit/${hotelId}`, payload);
      if (res?.status === 200) {
        toast.success('Datos del hotel guardados');
        onNext();
      } else {
        toast.error(res?.response?.data?.error || 'No se pudieron guardar los datos');
      }
    } catch (e) {
      console.error(e);
      toast.error('Error guardando los datos del hotel');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" gutterBottom>
          Datos de tu hotel
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Esta información aparece en tus facturas y controla los horarios de operación. Puedes completarla o cambiarla después en
          Configuración del Hotel.
        </Typography>
      </Box>

      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Número de identificación fiscal (RIF / NIT)"
            value={form.gstNumber}
            onChange={set('gstNumber')}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Teléfono del hotel"
            type="tel"
            placeholder="+58 212..."
            helperText="Incluye el código del país"
            value={form.contact}
            onChange={set('contact')}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth label="Dirección" value={form.address} onChange={set('address')} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            fullWidth
            label="% Impuesto habitaciones"
            type="number"
            inputProps={{ min: 0, max: 100 }}
            value={form.roomgstpercentage}
            onChange={set('roomgstpercentage')}
            helperText="Si no aplica, deja vacío"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            fullWidth
            label="% Impuesto alimentos"
            type="number"
            inputProps={{ min: 0, max: 100 }}
            value={form.foodgstpercentage}
            onChange={set('foodgstpercentage')}
            helperText="Si no aplica, deja vacío"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            fullWidth
            label="Hora de check-in"
            type="time"
            value={form.checkInTime}
            onChange={set('checkInTime')}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            fullWidth
            label="Hora de check-out"
            type="time"
            value={form.checkOutTime}
            onChange={set('checkOutTime')}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Tiempo de limpieza entre estancias (minutos)"
            type="number"
            inputProps={{ min: 0, max: 240 }}
            value={form.cleaningBufferMinutes}
            onChange={set('cleaningBufferMinutes')}
          />
        </Grid>
        <Grid item xs={12}>
          <Alert severity="info">
            Recomendamos <strong>30 minutos</strong> de limpieza: es el tiempo que el sistema bloquea cada habitación entre una salida
            y la siguiente entrada para que housekeeping la prepare.
          </Alert>
        </Grid>
      </Grid>

      <Divider />

      <Stack direction="row" justifyContent="space-between">
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ textTransform: 'none' }} disabled={saving}>
          Atrás
        </Button>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" onClick={onNext} disabled={saving} sx={{ textTransform: 'none' }}>
            Omitir por ahora
          </Button>
          <Button variant="contained" onClick={handleNext} disabled={saving} sx={{ px: 4, fontWeight: 600 }}>
            {saving ? 'Guardando...' : 'Guardar y continuar'}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}
